import { useEffect, useLayoutEffect, useRef, useState } from "react";

type AsyncEffectCallback = () => Promise<void | (() => void)>;
type DependencyList = ReadonlyArray<unknown>;

export function useAsyncEffect(
  effect: AsyncEffectCallback,
  deps?: DependencyList
) {
  useEffect(() => {
    let isActive = true;
    let cleanup: void | (() => void);

    (async () => {
      try {
        const result = await effect();
        if (isActive) {
          cleanup = result ?? undefined;
        }
      } catch (err) {
        console.error("useAsyncEffect error:", err);
      }
    })();

    return () => {
      isActive = false;
      if (cleanup) cleanup();
    };
  }, deps);
}

export function useComponentSize<T extends HTMLElement | null>(
  ref: React.RefObject<T>
) {
  const [rect, setRect] = useState<DOMRectReadOnly | null>(null);

  // Avoid SSR warnings: useLayoutEffect in browser, useEffect on server
  const useIsoLayoutEffect =
    typeof window !== "undefined" ? useLayoutEffect : useEffect;

  useIsoLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      const next = el.getBoundingClientRect();
      setRect((prev) => {
        if (
          prev &&
          prev.x === next.x &&
          prev.y === next.y &&
          prev.width === next.width &&
          prev.height === next.height
        ) {
          return prev;
        }
        return next; // DOMRectReadOnly
      });
    };

    update();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => update());
      ro.observe(el);
      return () => ro.disconnect();
    }

    const onResize = () => update();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return rect;
}
